import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Plus,
  Hash,
  Send,
  Loader2,
  MessageSquare,
  Paperclip,
  Image as ImageIcon,
  Smile,
  Share2,
  UserPlus,
  User as UserIcon,
  X,
  FileText,
  CornerUpLeft,
  Pencil,
  Trash2,
  Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import { chatApi, jobsApi, estimatesApi, customersApi, pushApi } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useChat } from "../../contexts/ChatContext";
import ChatAvatar, { colorFor, initialsFor } from "../../components/chat/ChatAvatar";
import { NewChannelModal, NewDmModal } from "../../components/chat/ChatModals";
import { buildReplyTree } from "../../lib/chatReplyTree";
import { useChatSearch } from "../../components/chat/useChatSearch";
import { ChatSearchDropdown } from "../../components/chat/ChatSearchDropdown";

/**
 * Mobile Messages — single-column, large-tap-target redesign.
 *
 * Two views inside one route:
 *   • ConversationList  — default; channels + DMs with search + "+"
 *   • ChatView          — full-screen once the user taps a row;
 *                         a back button returns to the list
 *
 * State is URL-driven via `?c=<conversationId>` so the bottom nav's
 * Messages tap always lands back on the list.
 *
 * Theme: technicians stay inside the same dark canvas as their
 * Dashboard/Calendar/Guru shell (matches `.cc-mobile`'s --cc-canvas /
 * --cc-surface defaults); admins/dispatchers keep the light "mv" look.
 *
 * Feature parity: send + receive text messages (with 3-second polling),
 * unread badges from ChatContext, inline quote-replies (tap → Reply)
 * message editing and deleting (tap own/any message → action sheet with
 * Reply / Edit (own) / Delete (own), loads Edit into the composer).
 * Reactions / side-panel threads remain desktop-only.
 */
function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  if (same) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const y = new Date(now.getTime() - 86400000);
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function initials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

const LIGHT = { page: "#E1E2E4", card: "#ffffff", border: "#e2e8f0", text: "#0f172a", sub: "#64748b", chip: "#0B1E3F1A", scope: "mv" };
const DARK = { page: "#0A1326", card: "#1E293B", border: "#22304D", text: "#F1F5F9", sub: "#FFFFFF", accent: "#60A5FA", chip: "rgba(255,255,255,0.07)", scope: "cc-mobile" };

export default function MobileMessages() {
  const { user } = useAuth();
  const { unread, refreshUnread, suppressNextNotify } = useChat();
  const [searchParams, setSearchParams] = useSearchParams();
  const isTech = user?.role === "technician";
  const T = isTech ? DARK : LIGHT;

  const [conversations, setConversations] = useState([]);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'channel' | 'dm'

  const activeId = searchParams.get("c") || null;

  const memberMap = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m])), [members]);

  const loadConvs = useCallback(async () => {
    try {
      const cs = await chatApi.conversations();
      setConversations(cs || []);
    } catch {
      setConversations([]);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([chatApi.members(), chatApi.conversations()])
      .then(([ms, cs]) => {
        if (cancelled) return;
        setMembers(ms || []);
        setConversations(cs || []);
      })
      .catch(() => { if (!cancelled) { setMembers([]); setConversations([]); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Poll conversations for updated unread counts every 8s (safety net for
  // very brief WS drops). Live updates arrive via the `chat-event` listener.
  useEffect(() => {
    const id = setInterval(() => { loadConvs(); }, 8000);
    return () => clearInterval(id);
  }, [loadConvs]);

  // Real-time: refresh conversation list whenever a chat event fires so
  // last-message + timestamp on rows stay live.
  useEffect(() => {
    const onEvt = () => loadConvs();
    window.addEventListener("chat-event", onEvt);
    return () => window.removeEventListener("chat-event", onEvt);
  }, [loadConvs]);

  const openConv = (id) => {
    setSearchParams((p) => { const n = new URLSearchParams(p); n.set("c", id); return n; }, { replace: false });
  };

  // Live search dropdown (channel names + message content within my conversations).
  const searchBoxRef = useRef(null);
  const { results: searchResults, open: searchOpen, setOpen: setSearchOpen, loading: searchLoading, runNow: runSearchNow } = useChatSearch(search);
  useEffect(() => {
    const onClick = (e) => { if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [setSearchOpen]);
  const goToSearchResult = (r) => {
    setSearch("");
    setSearchOpen(false);
    setSearchParams((p) => {
      const n = new URLSearchParams(p);
      n.set("c", r.conversationId);
      if (r.messageId) n.set("m", r.messageId); else n.delete("m");
      return n;
    }, { replace: false });
  };

  const handleCreated = async (conv) => {
    setModal(null);
    await loadConvs();
    openConv(conv.id);
  };

  const backToList = () => {
    setSearchParams((p) => { const n = new URLSearchParams(p); n.delete("c"); return n; }, { replace: false });
  };

  if (activeId) {
    const conv = conversations.find((c) => c.id === activeId);
    return (
      <MobileChatView
        conv={conv}
        conversationId={activeId}
        memberMap={memberMap}
        members={members}
        meId={user?.id}
        T={T}
        onBack={backToList}
        onSent={() => { loadConvs(); refreshUnread && refreshUnread(); suppressNextNotify && suppressNextNotify(); }}
      />
    );
  }

  // -------- Conversation list --------
  const filt = (c) => !search || (c.name || "").toLowerCase().includes(search.toLowerCase());
  const channels = conversations.filter((c) => (c.type === "channel" || c.type === "group") && filt(c));
  const dms = conversations.filter((c) => c.type === "dm" && filt(c));

  return (
    <div
      className={`${T.scope} md:hidden min-h-screen w-full`}
      style={{
        color: T.text,
        paddingTop: "calc(3.5rem + env(safe-area-inset-top))",
        paddingBottom: "calc(5rem + env(safe-area-inset-bottom))",
      }}
      data-testid="mobile-messages"
    >
      {isTech && (
        <header
          className="fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-slate-200 flex items-center justify-center px-4"
          data-testid="mobile-messages-tech-header"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          <div className="text-lg font-black text-[#0B1E3F] tracking-tight">Messages</div>
        </header>
      )}
      {/* Search + new */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-2">
        <div className="relative flex-1" ref={searchBoxRef}>
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" color={T.sub} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations"
            data-testid="mobile-messages-search"
            className="w-full h-11 pl-9 pr-3 rounded-full text-[15px] border"
            style={{ backgroundColor: isTech ? T.card : "#F8FAFC", borderColor: isTech ? T.border : "#262626", color: T.text }}
          />
          {searchOpen && (
            <ChatSearchDropdown
              results={searchResults}
              loading={searchLoading}
              query={search}
              onSelect={goToSearchResult}
              style={{ bg: T.card, border: T.border, text: T.text, sub: T.sub, hoverBg: isTech ? "rgba(255,255,255,0.06)" : "#F8FAFC" }}
            />
          )}
        </div>
        <button
          onClick={() => runSearchNow(search)}
          data-testid="mobile-messages-search-trigger"
          aria-label="Search"
          className="h-11 w-11 rounded-full flex items-center justify-center active:opacity-90"
          style={{ backgroundColor: "#2563EB", color: "#ffffff" }}
        >
          <Search className="h-5 w-5" strokeWidth={2.5} />
        </button>
      </div>

      {modal === "channel" && (
        <NewChannelModal members={members} currentUserId={user?.id} onClose={() => setModal(null)} onCreated={handleCreated} />
      )}
      {modal === "dm" && (
        <NewDmModal members={members} currentUserId={user?.id} onClose={() => setModal(null)} onStarted={handleCreated} />
      )}

      {loading ? (
        <div className="mt-6 flex items-center justify-center text-[14px]" style={{ color: T.sub }}>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading conversations…
        </div>
      ) : (
        <>
          <SectionHeader
            label="Channels"
            color={T.accent || T.sub}
            border={T.accent}
            action={
              <button
                onClick={() => setModal("channel")}
                data-testid="mobile-create-channel-btn"
                className="flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-full active:opacity-80"
                style={{ backgroundColor: T.chip, color: T.accent || T.text }}
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2.5} /> Create Channel
              </button>
            }
          />
          <ul className="divide-y" style={{ backgroundColor: T.card, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }} data-testid="mobile-messages-channels">
            {channels.map((c) => (
              <ConvRow
                key={c.id}
                conv={c}
                unread={unread?.byConversation?.[c.id] || 0}
                onClick={() => openConv(c.id)}
                left={<HashAvatar />}
                T={T}
              />
            ))}
          </ul>

          <SectionHeader
            label="Direct Messages"
            color={T.accent || T.sub}
            border={T.accent}
            action={
              <button
                onClick={() => setModal("dm")}
                data-testid="mobile-new-dm-btn"
                className="flex items-center gap-1 text-[12px] font-semibold px-2.5 py-1 rounded-full active:opacity-80"
                style={{ backgroundColor: T.chip, color: T.accent || T.text }}
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2.5} /> New DM
              </button>
            }
          />
          <ul className="divide-y" style={{ backgroundColor: T.card, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }} data-testid="mobile-messages-dms">
            {dms.map((c) => {
              const other = (c.memberIds || []).find((id) => id !== user?.id);
              const m = memberMap[other] || {};
              return (
                <ConvRow
                  key={c.id}
                  conv={{ ...c, name: c.name || m.name || "Direct message" }}
                  unread={unread?.byConversation?.[c.id] || 0}
                  onClick={() => openConv(c.id)}
                  left={<InitialAvatar name={c.name || m.name || "?"} presence={m.presence} />}
                  T={T}
                />
              );
            })}
          </ul>

          {channels.length === 0 && dms.length === 0 && (
            <div className="px-6 pt-16 text-center">
              <MessageSquare className="h-10 w-10 mx-auto mb-3" color={T.sub} />
              <div className="text-[15px] font-semibold" style={{ color: T.text }}>No conversations yet</div>
              <div className="text-[13px] mt-1" style={{ color: T.sub }}>
                Use "Create Channel" or "New DM" above to get started.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SectionHeader({ label, color, border, action }) {
  return (
    <div className="px-5 pt-5 pb-1.5 flex items-center justify-between gap-2">
      <span
        className="text-[12px] font-semibold uppercase tracking-wide"
        style={border ? { color, borderLeft: `3px solid ${border}`, paddingLeft: 8 } : { color }}
      >
        {label}
      </span>
      {action}
    </div>
  );
}

function ConvRow({ conv, unread, onClick, left, T }) {
  return (
    <li>
      <button
        onClick={onClick}
        data-testid={`mobile-messages-row-${conv.id}`}
        className="w-full text-left px-4 py-3 flex items-center gap-3 active:opacity-80"
        style={{ borderColor: T.border }}
      >
        {left}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className={`text-[15.5px] truncate ${unread > 0 ? "font-bold" : "font-semibold"}`} style={{ color: T.text }}>
              {conv.name}
            </div>
            <div className="ml-auto text-[11.5px] shrink-0" style={{ color: T.sub }}>
              {fmtTime(conv.lastMessageAt)}
            </div>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <div className="text-[13px] truncate flex-1" style={{ color: unread > 0 ? T.text : T.sub }}>
              {conv.lastMessage?.text || "No messages yet"}
            </div>
            {unread > 0 && (
              <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-[11px] font-bold"
                    style={{ backgroundColor: "#2563EB", color: "#ffffff" }}>
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

function HashAvatar() {
  return (
    <div className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#DBEAFE" }}>
      <Hash className="h-5 w-5" color="#2563EB" strokeWidth={2.5} />
    </div>
  );
}

function InitialAvatar({ name, presence }) {
  return (
    <div className="relative h-11 w-11 shrink-0">
      <div className="h-11 w-11 rounded-full flex items-center justify-center text-[14px] font-bold" style={{ backgroundColor: "#EDE9FE", color: "#6D28D9" }}>
        {initials(name)}
      </div>
      {presence === "online" && (
        <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white" style={{ backgroundColor: "#10B981" }} />
      )}
    </div>
  );
}

// ---------------- Chat view ----------------

function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const t = new Date();
  const y = new Date(t.getTime() - 86400000);
  const same = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, t)) return "TODAY";
  if (same(d, y)) return "YESTERDAY";
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}

function timeLabel(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** Larger square avatar used inside the message stream (matches reference image). */
function BigAvatar({ name, avatarUrl, size = 44 }) {
  const src = avatarUrl ? chatApi.fileUrl(avatarUrl) : null;
  return (
    <div
      className="rounded-xl flex items-center justify-center font-bold text-white overflow-hidden shrink-0"
      style={{ width: size, height: size, background: colorFor(name || ""), fontSize: 16 }}
    >
      {src ? <img src={src} alt={name} className="h-full w-full object-cover" /> : initialsFor(name || "?")}
    </div>
  );
}

function MessageText({ text, color }) {
  const parts = String(text || "").split(/(@[\w.']+)/g);
  return (
    <div className="text-[15px] leading-[1.45] whitespace-pre-wrap break-words" style={{ color }}>
      {parts.map((p, i) =>
        p.startsWith("@")
          ? <span key={i} className="font-semibold" style={{ color: "#3B82F6" }}>{p}</span>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </div>
  );
}

const SHARE_ICON = { job: Briefcase, estimate: FileText, customer: UserIcon };

function fmtShareDate(iso) {
  if (!iso) return "TBD";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "TBD" : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function SharedCard({ sref }) {
  const navigate = useNavigate();
  const Icon = SHARE_ICON[sref.kind] || FileText;
  return (
    <button
      onClick={() => sref.url && navigate(sref.url)}
      className="mt-1.5 flex flex-col rounded-xl px-4 py-3 w-full max-w-[280px] text-left active:opacity-90"
      style={{ backgroundColor: "#0B1E3F", color: "#ffffff" }}
      data-testid="mobile-shared-card"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(255,255,255,0.08)" }}>
          <Icon className="h-3.5 w-3.5" color="#93c5fd" />
        </div>
        <span className="text-[10.5px] font-bold uppercase tracking-widest" style={{ color: "#94a3b8" }}>{sref.kind}</span>
      </div>
      {sref.kind === "job" && (
        <>
          <div className="text-[15px] font-bold truncate">{sref.jobNumber}</div>
          <div className="text-[13px] truncate" style={{ color: "#cbd5e1" }}>{sref.customerName || "—"}</div>
          <div className="flex items-center justify-between mt-1.5 pt-1.5" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <span className="text-[11.5px] font-semibold" style={{ color: "#93c5fd" }}>{sref.status || "—"}</span>
            <span className="text-[11.5px]" style={{ color: "#94a3b8" }}>{fmtShareDate(sref.scheduledAt)}</span>
          </div>
        </>
      )}
      {sref.kind === "estimate" && (
        <>
          <div className="text-[15px] font-bold truncate">{sref.estimateNumber}</div>
          <div className="text-[13px] truncate" style={{ color: "#cbd5e1" }}>{sref.customerName || "—"}</div>
          <div className="flex items-center justify-between mt-1.5 pt-1.5" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <span className="text-[11.5px] font-semibold" style={{ color: "#93c5fd" }}>{sref.status || "—"}</span>
            <span className="text-[15px] font-black">${Math.round(Number(sref.total || 0)).toLocaleString()}</span>
          </div>
        </>
      )}
      {sref.kind === "customer" && (
        <>
          <div className="text-[15px] font-bold truncate">{sref.customerName}</div>
          <div className="text-[13px] mt-0.5 truncate" style={{ color: "#cbd5e1" }}>{sref.phone || "—"}</div>
          <div className="text-[13px] truncate" style={{ color: "#cbd5e1" }}>{sref.email || "—"}</div>
        </>
      )}
    </button>
  );
}

/* ---------------- Share picker (Job / Estimate / Customer → rich card) ---------------- */

const SHARE_KINDS = [
  {
    kind: "job", label: "Job", icon: Briefcase,
    load: () => jobsApi.list(),
    labelOf: (j) => j.id, sub: (j) => j.customer || j.status,
    matches: (j, q) => `${j.id} ${j.customer || ""}`.toLowerCase().includes(q),
  },
  {
    kind: "estimate", label: "Estimate", icon: FileText,
    load: () => estimatesApi.list(),
    labelOf: (e) => e.number || e.id, sub: (e) => e.customer,
    matches: (e, q) => `${e.number || e.id}`.toLowerCase().includes(q),
  },
  {
    kind: "customer", label: "Customer", icon: UserIcon,
    load: () => customersApi.list(),
    labelOf: (c) => c.name, sub: (c) => c.phone || c.email,
    matches: (c, q) => `${c.name || ""}`.toLowerCase().includes(q),
  },
];

function buildSharedRef(kind, it) {
  if (kind === "job") {
    return {
      kind, id: it.id, label: it.id, sub: it.customer, url: `/mobile/jobs/${it.id}`,
      jobNumber: it.id, customerName: it.customer, status: it.status, scheduledAt: it.start,
    };
  }
  if (kind === "estimate") {
    const number = it.number || it.id;
    return {
      kind, id: it.id, label: number, sub: it.customer, url: `/estimates?id=${it.id}`,
      estimateNumber: number, customerName: it.customer, total: it.total, status: it.status,
    };
  }
  return {
    kind, id: it.id, label: it.name, sub: it.phone || it.email, url: `/customers?id=${it.id}`,
    customerName: it.name, phone: it.phone, email: it.email,
  };
}

function MobileShareSheet({ onClose, onPick, T }) {
  const [kind, setKind] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const cfg = SHARE_KINDS.find((k) => k.kind === kind);

  useEffect(() => {
    if (!cfg) return;
    let alive = true;
    setLoading(true);
    setQ("");
    cfg.load()
      .then((r) => { if (alive) setItems(r || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const filtered = cfg ? items.filter((it) => !q.trim() || cfg.matches(it, q.trim().toLowerCase())) : [];

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center" onClick={onClose} data-testid="mobile-share-sheet">
      <div className="absolute inset-0 bg-slate-900/50" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full rounded-t-2xl overflow-hidden max-h-[80vh] flex flex-col"
        style={{ backgroundColor: T.card, paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="h-1 w-10 rounded-full mx-auto mt-2.5 mb-1" style={{ backgroundColor: T.border }} />
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: `1px solid ${T.border}` }}>
          {cfg ? (
            <button onClick={() => setKind(null)} data-testid="mobile-share-back" className="text-[13px] font-semibold active:opacity-70" style={{ color: "#60A5FA" }}>← Back</button>
          ) : (
            <div className="text-[15px] font-bold" style={{ color: T.text }}>Share to chat</div>
          )}
          <button onClick={onClose} data-testid="mobile-share-close" aria-label="Close" className="h-7 w-7 rounded-md flex items-center justify-center active:opacity-70">
            <X className="h-4 w-4" color={T.sub} />
          </button>
        </div>

        {!cfg ? (
          <div className="px-5 py-4 space-y-2 pb-6">
            {SHARE_KINDS.map((k) => {
              const Icon = k.icon;
              return (
                <button
                  key={k.kind}
                  onClick={() => setKind(k.kind)}
                  data-testid={`mobile-share-kind-${k.kind}`}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl active:opacity-80"
                  style={{ border: `1px solid ${T.border}` }}
                >
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: "rgba(139,92,246,0.15)" }}>
                    <Icon className="h-5 w-5" color="#8B5CF6" />
                  </div>
                  <span className="text-[15px] font-semibold" style={{ color: T.text }}>Share a {k.label}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 pb-5">
            <div className="relative my-3">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" color={T.sub} />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search ${cfg.label.toLowerCase()}s…`}
                data-testid="mobile-share-search"
                className="w-full h-10 pl-8 pr-3 rounded-md text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                style={{ backgroundColor: T.chip, color: T.text, border: `1px solid ${T.border}` }}
              />
            </div>
            {loading && <div className="text-[12.5px] text-center py-6" style={{ color: T.sub }}>Loading…</div>}
            {!loading && filtered.length === 0 && (
              <div className="text-[12.5px] text-center py-6" style={{ color: T.sub }}>Nothing to share here yet.</div>
            )}
            <div className="space-y-1.5 pb-2">
              {filtered.slice(0, 50).map((it) => {
                const Icon = cfg.icon;
                return (
                  <button
                    key={it.id}
                    onClick={() => onPick(kind, it)}
                    data-testid={`mobile-share-item-${it.id}`}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg active:opacity-80 text-left"
                    style={{ border: `1px solid ${T.border}` }}
                  >
                    <div className="h-8 w-8 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: T.chip }}>
                      <Icon className="h-4 w-4" color={T.sub} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-semibold truncate" style={{ color: T.text }}>{cfg.labelOf(it)}</div>
                      <div className="text-[11.5px] truncate" style={{ color: T.sub }}>{cfg.sub(it)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentChip({ a, T }) {
  if (a.kind === "image") {
    return (
      <a
        href={chatApi.fileUrl(a.url)}
        target="_blank"
        rel="noreferrer"
        data-testid="mobile-attachment-image"
        className="block mt-1.5 rounded-xl overflow-hidden w-fit"
      >
        <img src={chatApi.fileUrl(a.url)} alt={a.filename} className="max-h-56 max-w-[80%] object-cover" />
      </a>
    );
  }
  return (
    <a
      href={chatApi.fileUrl(a.url)}
      target="_blank"
      rel="noreferrer"
      data-testid="mobile-attachment-file"
      className="mt-1.5 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[14px] font-semibold"
      style={{ backgroundColor: "#0B1E3F", color: "#ffffff", maxWidth: "80%" }}
    >
      <Paperclip className="h-4 w-4 shrink-0" color="#93c5fd" />
      <span className="truncate">{a.filename}</span>
    </a>
  );
}

function MessageRow({ m, sender, memberMap, meId, T, onOpenActions }) {
  const handleTap = (e) => {
    if (m.deleted) return;
    if (e.target.closest("a, button")) return; // let attachment links / shared-card taps behave normally
    onOpenActions?.(m);
  };

  if (m.deleted) {
    return (
      <div className="flex gap-3 mt-3 px-3">
        <BigAvatar name={sender?.name || "?"} avatarUrl={sender?.avatarUrl} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[15px] font-bold" style={{ color: T.text }}>{sender?.name || "Unknown"}</span>
            <span className="text-[11px]" style={{ color: T.sub }}>{timeLabel(m.createdAt)}</span>
          </div>
          <div className="text-[13.5px] italic" style={{ color: T.sub }}>This message was deleted</div>
        </div>
      </div>
    );
  }
  const replyToName = m.replyTo ? (memberMap?.[m.replyTo.senderId]?.name || "Unknown") : null;
  return (
    <div
      className="flex gap-3 mt-3 px-3 active:opacity-90"
      data-testid={`mobile-chat-message-${m.id}`}
      onClick={handleTap}
    >
      <BigAvatar name={sender?.name || "?"} avatarUrl={sender?.avatarUrl} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[15px] font-bold" style={{ color: T.text }}>{sender?.name || "Unknown"}</span>
          <span className="text-[11px]" style={{ color: T.sub }}>{timeLabel(m.createdAt)}</span>
        </div>
        {m.replyTo && (
          <div className="mt-1 mb-1 pl-2 border-l-2 flex items-baseline gap-1.5" style={{ borderColor: "#60A5FA" }} data-testid={`mobile-reply-quote-${m.id}`}>
            <span className="text-[12px] font-semibold shrink-0" style={{ color: "#60A5FA" }}>{replyToName}</span>
            <span className="text-[12px] truncate" style={{ color: T.sub }}>{m.replyTo.deleted ? "This message was deleted" : (m.replyTo.text || "Attachment")}</span>
          </div>
        )}
        {m.text && <MessageText text={m.text} color={T.text} />}
        {(m.attachments || []).map((a) => <AttachmentChip key={a.id} a={a} T={T} />)}
        {m.sharedRef && <SharedCard sref={m.sharedRef} />}
        {m.editedAt && <div className="text-[11px] mt-0.5" style={{ color: T.sub }} data-testid={`mobile-edited-label-${m.id}`}>(edited)</div>}
      </div>
    </div>
  );
}

function MobileChatView({ conv, conversationId, memberMap, members, meId, T, onBack, onSent }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [mention, setMention] = useState(null);
  const [mentionIds, setMentionIds] = useState([]);
  const [actionsTarget, setActionsTarget] = useState(null); // message shown in the tap action sheet
  const [replyTarget, setReplyTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const scrollRef = useRef(null);
  const lastTsRef = useRef(null);
  const taRef = useRef(null);
  const imgRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [det, msgs] = await Promise.all([
        chatApi.getConversation(conversationId).catch(() => null),
        chatApi.messages(conversationId, { limit: 60 }),
      ]);
      setMessages(msgs || []);
      lastTsRef.current = msgs && msgs.length ? msgs[msgs.length - 1].createdAt : null;
      await chatApi.markRead(conversationId).catch(() => {});
      void det;
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // "Currently active on this exact screen" signal for push suppression.
  useEffect(() => {
    const ping = () => pushApi.setActiveContext("conversation", conversationId).catch(() => {});
    ping();
    const id = setInterval(ping, 30000);
    return () => { clearInterval(id); pushApi.clearActiveContext().catch(() => {}); };
  }, [conversationId]);

  // Deep-link from a push notification (?m=<messageId>) — scroll to and
  // briefly highlight the message that triggered it, then clear the param.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const mid = searchParams.get("m");
    if (!mid || messages.length === 0) return;
    const el = document.querySelector(`[data-testid="mobile-chat-message-${mid}"]`);
    if (el) {
      el.scrollIntoView({ block: "center" });
      el.classList.add("ring-2", "ring-blue-400/60");
      setTimeout(() => el.classList.remove("ring-2", "ring-blue-400/60"), 2200);
    }
    setSearchParams((p) => { const n = new URLSearchParams(p); n.delete("m"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Poll for new messages every 3s (WebSocket is the primary path; this is
  // a safety net for very brief socket drops).
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const after = lastTsRef.current;
        const fresh = await chatApi.messages(conversationId, after ? { after } : { limit: 60 });
        if (fresh.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const add = fresh.filter((m) => !seen.has(m.id) && !m.parentId);
            return [...prev, ...add];
          });
          lastTsRef.current = fresh[fresh.length - 1].createdAt;
          chatApi.markRead(conversationId).catch(() => {});
        }
      } catch { /* ignore */ }
    }, 3000);
    return () => clearInterval(id);
  }, [conversationId]);

  // Real-time WebSocket delivery: merge new/edited/deleted messages instantly.
  useEffect(() => {
    const onEvt = (e) => {
      const d = e.detail || {};
      if (d.conversationId !== conversationId) return;
      if (d.type === "chat_message" && d.message) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === d.message.id)) return prev; // already applied optimistically
          if (d.message.parentId) return prev; // top-level stream only
          return [...prev, d.message];
        });
        lastTsRef.current = d.message.createdAt;
        chatApi.markRead(conversationId).catch(() => {});
      } else if (d.type === "chat_message_updated" && d.message) {
        setMessages((prev) => prev.map((m) => m.id === d.message.id ? d.message : m));
      } else if (d.type === "chat_message_deleted" && d.messageId) {
        setMessages((prev) => prev.map((m) => m.id === d.messageId ? { ...m, deleted: true, text: "", attachments: [], sharedRef: null } : m));
      }
    };
    window.addEventListener("chat-event", onEvt);
    return () => window.removeEventListener("chat-event", onEvt);
  }, [conversationId]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (editTarget) {
      setText(editTarget.text || "");
      taRef.current?.focus();
    }
  }, [editTarget]);

  const onChangeText = (e) => {
    const val = e.target.value;
    setText(val);
    const upto = val.slice(0, e.target.selectionStart);
    const mtch = upto.match(/@([\w.']*)$/);
    setMention(mtch ? { query: mtch[1] } : null);
  };

  const mentionList = mention
    ? (members || []).filter((m) => m.id !== meId && (m.name || "").toLowerCase().includes(mention.query.toLowerCase())).slice(0, 6)
    : [];

  const pickMention = (m) => {
    setText((t) => t.replace(/@([\w.']*)$/, `@${m.name.replace(/\s+/g, "")} `));
    setMentionIds((ids) => [...new Set([...ids, m.id])]);
    setMention(null);
    taRef.current?.focus();
  };

  const stageFiles = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const a = await chatApi.uploadAttachment(f);
        setAttachments((prev) => [...prev, a]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const send = async () => {
    const body = text.trim();
    if (sending) return;
    if (editTarget) {
      if (!body) return;
      setSending(true);
      try {
        const u = await chatApi.edit(editTarget.id, body);
        setMessages((prev) => prev.map((x) => (x.id === u.id ? u : x)));
        setEditTarget(null);
        setText("");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not save edit");
      } finally {
        setSending(false);
      }
      return;
    }
    if (!body && !attachments.length) return;
    setSending(true);
    try {
      const usedMentions = mentionIds.filter((id) => {
        const m = (members || []).find((x) => x.id === id);
        return m && body.includes(`@${m.name.replace(/\s+/g, "")}`);
      });
      const msg = await chatApi.send(conversationId, { text: body, attachments, mentions: usedMentions, replyToId: replyTarget?.id || null });
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      lastTsRef.current = msg.createdAt;
      setText("");
      setAttachments([]);
      setMentionIds([]);
      setReplyTarget(null);
      onSent && onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (m) => {
    setActionsTarget(null);
    try {
      await chatApi.remove(m.id);
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, deleted: true, text: "", attachments: [], sharedRef: null } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete message");
    }
  };

  const handleShare = async (kind, item) => {    setShareOpen(false);
    try {
      const sharedRef = buildSharedRef(kind, item);
      const msg = await chatApi.send(conversationId, { sharedRef });
      setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      lastTsRef.current = msg.createdAt;
      onSent && onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not share");
    }
  };

  const title = conv?.name || "Conversation";
  const memberCount = conv?.memberIds?.length || 0;
  const subtitle = [
    memberCount ? `${memberCount} member${memberCount === 1 ? "" : "s"}` : null,
    conv?.description || null,
  ].filter(Boolean).join(" · ");

  // Group messages into day buckets for divider rendering; inline replies are
  // nested directly under their target message (buildReplyTree), matching desktop.
  const rows = [];
  let lastDay = null;
  buildReplyTree(messages).forEach(({ msg: m, isReply }) => {
    const d = dayLabel(m.createdAt);
    if (d !== lastDay) { rows.push({ divider: d, key: `d-${m.id}` }); lastDay = d; }
    rows.push({ msg: m, isReply, key: m.id });
  });

  return (
    <div
      className={`${T.scope} md:hidden fixed inset-0 z-[60] flex flex-col`}
      style={{ backgroundColor: T.page }}
      data-testid="mobile-chat-view"
    >
      {/* Dark navy header (matches app sidebar on both themes) */}
      <div
        className="shrink-0 flex items-center px-2 gap-2"
        style={{
          backgroundColor: "#0B1E3F",
          height: "3.75rem",
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        <button
          onClick={onBack}
          data-testid="mobile-chat-back"
          className="h-10 w-10 rounded-full flex items-center justify-center active:bg-white/10"
          aria-label="Back"
        >
          <ArrowLeft className="h-6 w-6" color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} />
        </button>
        <div className="h-9 w-9 rounded-lg shrink-0 flex items-center justify-center" style={{ backgroundColor: "rgba(147,197,253,0.14)" }}>
          <Hash className="h-5 w-5" color="#93c5fd" strokeWidth={2.5} style={{ color: "#93c5fd", stroke: "#93c5fd" }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[16px] font-bold truncate" style={{ color: "#ffffff" }}>{title}</div>
          {subtitle && <div className="text-[11.5px] truncate" style={{ color: "#cbd5e1" }}>{subtitle}</div>}
        </div>
        {conv?.type !== "dm" && (
          <button
            data-testid="mobile-chat-add"
            className="h-9 px-3 rounded-full inline-flex items-center gap-1.5 active:opacity-90"
            style={{ backgroundColor: "rgba(255,255,255,0.10)", color: "#ffffff" }}
            onClick={() => toast.info("Add members from desktop for now.")}
          >
            <UserPlus className="h-4 w-4" color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} /> <span className="text-[13px] font-semibold" style={{ color: "#ffffff" }}>Add</span>
          </button>
        )}
      </div>

      {/* Message stream */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto py-2" data-testid="mobile-chat-stream" style={{ backgroundColor: T.page }}>
        {loading && messages.length === 0 && (
          <div className="text-center py-10" style={{ color: T.sub }}>
            <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading messages…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-center py-10 text-[13px]" style={{ color: T.sub }}>
            No messages yet — say hello 👋
          </div>
        )}
        {rows.map((r) => {
          if (r.divider) {
            return (
              <div key={r.key} className="flex items-center gap-3 my-3 px-3" data-testid={`mobile-day-divider-${r.divider}`}>
                <div className="flex-1 h-px" style={{ backgroundColor: T.border }} />
                <span className="text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: T.sub }}>{r.divider}</span>
                <div className="flex-1 h-px" style={{ backgroundColor: T.border }} />
              </div>
            );
          }
          return (
            <div key={r.key} className={r.isReply ? "ml-9 pl-3 border-l-2" : ""} style={r.isReply ? { borderColor: T.border } : undefined} data-testid={r.isReply ? `mobile-reply-row-${r.msg.id}` : undefined}>
              <MessageRow m={r.msg} sender={memberMap[r.msg.senderId]} memberMap={memberMap} meId={meId} T={T} onOpenActions={setActionsTarget} />
            </div>
          );
        })}
      </div>

      {/* Tap message action sheet — Reply (always) + Edit/Delete (own messages) */}
      {actionsTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center"
          onClick={() => setActionsTarget(null)}
          data-testid="mobile-message-actions-sheet"
        >
          <div className="absolute inset-0 bg-slate-900/50" />
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full rounded-t-2xl overflow-hidden"
            style={{ backgroundColor: T.card, paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <div className="h-1 w-10 rounded-full mx-auto mt-2.5 mb-1" style={{ backgroundColor: T.border }} />
            <button
              onClick={() => { setEditTarget(null); setReplyTarget(actionsTarget); setActionsTarget(null); }}
              data-testid="mobile-action-reply-btn"
              className="w-full flex items-center gap-3 px-5 py-3.5 active:opacity-80"
              style={{ borderTop: `1px solid ${T.border}` }}
            >
              <CornerUpLeft className="h-5 w-5" color={T.text} />
              <span className="text-[15px] font-semibold" style={{ color: T.text }}>Reply</span>
            </button>
            {actionsTarget.senderId === meId && (
              <button
                onClick={() => { setReplyTarget(null); setEditTarget(actionsTarget); setActionsTarget(null); }}
                data-testid="mobile-action-edit-btn"
                className="w-full flex items-center gap-3 px-5 py-3.5 active:opacity-80"
                style={{ borderTop: `1px solid ${T.border}` }}
              >
                <Pencil className="h-5 w-5" color={T.text} />
                <span className="text-[15px] font-semibold" style={{ color: T.text }}>Edit message</span>
              </button>
            )}
            {actionsTarget.senderId === meId && (
              <button
                onClick={() => handleDelete(actionsTarget)}
                data-testid="mobile-action-delete-btn"
                className="w-full flex items-center gap-3 px-5 py-3.5 pb-5 active:opacity-80"
                style={{ borderTop: `1px solid ${T.border}` }}
              >
                <Trash2 className="h-5 w-5" color="#EF4444" />
                <span className="text-[15px] font-semibold" style={{ color: "#EF4444" }}>Delete message</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Share picker — Job / Estimate / Customer → rich card in the channel */}
      {shareOpen && <MobileShareSheet T={T} onClose={() => setShareOpen(false)} onPick={handleShare} />}

      {/* Composer */}
      <div
        className="shrink-0 px-3 pt-2"
        style={{
          backgroundColor: T.page,
          paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
        }}
      >
        {/* Reply / edit banner */}
        {editTarget && (
          <div className="flex items-center justify-between mb-2 pl-3 pr-2 py-1.5 rounded-md" style={{ backgroundColor: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)" }} data-testid="mobile-edit-banner">
            <div className="flex items-center gap-2 min-w-0">
              <Pencil className="h-3.5 w-3.5 shrink-0" color="#F59E0B" />
              <span className="text-[12.5px] font-semibold" style={{ color: "#F59E0B" }}>Editing message</span>
            </div>
            <button onClick={() => { setEditTarget(null); setText(""); }} data-testid="mobile-edit-cancel-btn" className="h-6 w-6 rounded flex items-center justify-center active:opacity-70">
              <X className="h-3.5 w-3.5" color="#F59E0B" />
            </button>
          </div>
        )}
        {!editTarget && replyTarget && (
          <div className="flex items-center justify-between mb-2 pl-3 pr-2 py-1.5 rounded-md" style={{ backgroundColor: "rgba(96,165,250,0.12)", border: "1px solid rgba(96,165,250,0.35)" }} data-testid="mobile-reply-banner">
            <div className="flex items-center gap-2 min-w-0">
              <CornerUpLeft className="h-3.5 w-3.5 shrink-0" color="#60A5FA" />
              <span className="text-[12.5px] font-semibold shrink-0" style={{ color: "#60A5FA" }}>Replying to {memberMap?.[replyTarget.senderId]?.name || "Unknown"}</span>
              <span className="text-[12px] truncate" style={{ color: T.sub }}>{replyTarget.deleted ? "This message was deleted" : (replyTarget.text || "Attachment")}</span>
            </div>
            <button onClick={() => setReplyTarget(null)} data-testid="mobile-reply-cancel-btn" className="h-6 w-6 rounded flex items-center justify-center active:opacity-70">
              <X className="h-3.5 w-3.5" color="#60A5FA" />
            </button>
          </div>
        )}

        {/* Staged attachments */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((a, i) => (
              <div key={a.id} className="flex items-center gap-1.5 h-7 pl-2 pr-1 rounded-full text-[12px]" style={{ backgroundColor: T.card, border: `1px solid ${T.border}`, color: T.sub }}>
                {a.kind === "image" ? <ImageIcon className="h-3.5 w-3.5" /> : <Paperclip className="h-3.5 w-3.5" />}
                <span className="truncate max-w-[140px]">{a.filename}</span>
                <button onClick={() => setAttachments((p) => p.filter((_, idx) => idx !== i))} className="h-5 w-5 rounded-full flex items-center justify-center active:opacity-70" aria-label="Remove attachment">
                  <X className="h-3.5 w-3.5" color={T.sub} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="relative flex items-end gap-1.5 rounded-2xl px-2 py-1.5" style={{ backgroundColor: T.chip }}>
          {/* Mention dropdown */}
          {mentionList.length > 0 && (
            <div className="absolute bottom-full left-2 mb-2 w-60 rounded-xl overflow-hidden z-30" style={{ backgroundColor: T.card, border: `1px solid ${T.border}`, boxShadow: "0 6px 24px rgba(15,23,42,0.35)" }} data-testid="mobile-mention-dropdown">
              {mentionList.map((m) => (
                <button
                  key={m.id}
                  onClick={() => pickMention(m)}
                  data-testid={`mobile-mention-option-${m.id}`}
                  className="w-full flex items-center gap-2 px-3 py-2 active:opacity-80 text-left"
                >
                  <ChatAvatar name={m.name} avatarUrl={m.avatarUrl} presence={m.presence} size="sm" showPresence={false} />
                  <span className="text-[13.5px] font-medium truncate" style={{ color: T.text }}>{m.name}</span>
                </button>
              ))}
            </div>
          )}
          {/* Emoji popover */}
          {showEmoji && (
            <div className="absolute bottom-full left-2 mb-2 w-64 grid grid-cols-8 gap-0.5 rounded-xl p-2 z-30" style={{ backgroundColor: T.card, border: `1px solid ${T.border}`, boxShadow: "0 6px 24px rgba(15,23,42,0.35)" }} data-testid="mobile-emoji-popover">
              {["👍","❤️","😂","🎉","🙌","🔥","👀","✅","🚀","😊","😍","🤔","😅","💯","👋","☕","🍕","🙏","👏","💪","😎","🤝","📞","📅","🛠️","🧰","🚚","🏠","💰","⚡","🌟","🎯"].map((e) => (
                <button
                  key={e}
                  onClick={() => { setText((t) => t + e); setShowEmoji(false); taRef.current?.focus(); }}
                  className="h-8 w-8 rounded active:opacity-70 text-[17px]"
                >
                  {e}
                </button>
              ))}
            </div>
          )}

          {/* Action icons — 36px square, single row (Layout Option A) */}
          <div className="flex items-center gap-1 pb-0.5">
            <button
              onClick={() => imgRef.current?.click()}
              disabled={!!editTarget}
              data-testid="mobile-composer-image"
              aria-label="Upload image"
              className="h-9 w-9 rounded-lg flex items-center justify-center shadow-sm active:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: "#0EA5E9" }}
            >
              <ImageIcon className="h-5 w-5 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={!!editTarget}
              data-testid="mobile-composer-file"
              aria-label="Attach file"
              className="h-9 w-9 rounded-lg flex items-center justify-center shadow-sm active:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: "#10B981" }}
            >
              <Paperclip className="h-5 w-5 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
            </button>
            <button
              onClick={() => setShowEmoji((v) => !v)}
              data-testid="mobile-composer-emoji"
              aria-label="Emoji"
              className="h-9 w-9 rounded-lg flex items-center justify-center shadow-sm active:opacity-90"
              style={{ backgroundColor: "#F59E0B" }}
            >
              <Smile className="h-5 w-5 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
            </button>
            <button
              onClick={() => setShareOpen(true)}
              disabled={!!editTarget}
              data-testid="mobile-composer-share"
              aria-label="Share a Job / Estimate / Customer"
              className="h-9 w-9 rounded-lg flex items-center justify-center shadow-sm active:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: "#8B5CF6" }}
            >
              <Share2 className="h-5 w-5 shrink-0" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
            </button>
          </div>

          {/* Input — expands to fill remaining space */}
          <textarea
            ref={taRef}
            value={text}
            onChange={onChangeText}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } if (e.key === "Escape" && editTarget) { setEditTarget(null); setText(""); } }}
            rows={1}
            placeholder={editTarget ? "Edit message…" : replyTarget ? "Reply…" : "Message…"}
            data-testid="mobile-chat-input"
            className="flex-1 resize-none bg-transparent px-2 py-1.5 text-[15px] max-h-32 min-h-[36px] focus:outline-none"
            style={{ color: T.text }}
          />

          {/* Send */}
          <div className="pb-0.5">
            <button
              onClick={send}
              disabled={uploading || sending || (!text.trim() && !attachments.length)}
              data-testid="mobile-chat-send"
              aria-label="Send"
              className="h-9 w-9 rounded-lg flex items-center justify-center shadow-sm disabled:opacity-50 active:opacity-90"
              style={{ backgroundColor: "#2563EB", color: "#ffffff" }}
            >
              {sending
                ? <Loader2 className="h-5 w-5 animate-spin" color="#ffffff" style={{ color: "#ffffff", stroke: "#ffffff" }} />
                : <Send className="h-5 w-5" color="#ffffff" strokeWidth={2.5} style={{ color: "#ffffff", stroke: "#ffffff" }} />
              }
            </button>
          </div>

          <input ref={imgRef} type="file" accept="image/*" hidden onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) stageFiles(f); }} />
          <input ref={fileRef} type="file" accept="application/pdf,.doc,.docx,.csv,.txt,image/*" hidden onChange={(e) => { const f = Array.from(e.target.files || []); e.target.value = ""; if (f.length) stageFiles(f); }} />
        </div>
        {uploading && <div className="text-[11px] mt-1" style={{ color: T.sub }}>Uploading…</div>}
      </div>
    </div>
  );
}
